/**
 * Extracts all participants (owner, permitted, joined) from a group object
 * and returns a unified array of participant objects with permission flags.
 * 
 * @param {Object} group - The group object containing users
 * @returns {Array} List of formatted participant objects
 */
export const getParticipants = (group) => {
  if (!group) return [];
  const map = new Map();

  // 1. Add Owner
  if (group.createdBy && typeof group.createdBy === "object") {
    map.set(group.createdBy._id.toString(), {
      ...group.createdBy,
      isOwner: true,
      isPermitted: true,
    });
  }

  // 2. Add Admins (Permitted Users)
  if (Array.isArray(group.permittedUsers)) {
    group.permittedUsers.forEach((u) => {
      if (u && typeof u === "object") {
        const uId = u._id.toString();
        if (!map.has(uId)) {
          map.set(uId, { ...u, isOwner: false, isPermitted: true });
        }
      }
    });
  }

  // 3. Add Normal Members (Joined Users)
  if (Array.isArray(group.joinedUsers)) {
    group.joinedUsers.forEach((u) => {
      if (u && typeof u === "object") {
        const uId = u._id.toString();
        if (!map.has(uId)) {
          map.set(uId, { ...u, isOwner: false, isPermitted: false });
        }
      }
    });
  }

  return Array.from(map.values());
};

/**
 * Returns the corresponding X and Y percentage coordinates for a given preset string.
 * 
 * @param {string} preset - The preset name (e.g., "bottom-right", "center")
 * @returns {Object|null} Object containing posX and posY, or null if unknown
 */
export const getPresetPositionValues = (preset) => {
  switch (preset) {
    case "bottom-right": return { posX: 85, posY: 85 };
    case "bottom-left": return { posX: 15, posY: 85 };
    case "top-right": return { posX: 85, posY: 15 };
    case "top-left": return { posX: 15, posY: 15 };
    case "center": return { posX: 50, posY: 50 };
    default: return null;
  }
};

/**
 * Calculates the percentage-based X/Y coordinates for a watermark
 * based on a mouse/touch event relative to the canvas bounding rectangle.
 * 
 * @param {number} clientX - The event's clientX
 * @param {number} clientY - The event's clientY
 * @param {DOMRect} rect - The canvas bounding rect
 * @returns {Object} Object containing bounded x and y percentages
 */
export const calculateWatermarkCoordinates = (clientX, clientY, rect) => {
  let x = ((clientX - rect.left) / rect.width) * 100;
  let y = ((clientY - rect.top) / rect.height) * 100;

  // Constrain coordinates between 5% and 95% to prevent going fully off-screen
  x = Math.max(5, Math.min(95, Math.round(x)));
  y = Math.max(5, Math.min(95, Math.round(y)));

  return { x, y };
};
